"use client";

import React, { useState, useCallback, useMemo } from "react";
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Flex,
  Tooltip,
  IconButton,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { Plus, LayoutGrid, Pencil, Trash2, Users } from "lucide-react";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";
import { VscCallOutgoing } from "react-icons/vsc";
import { TbCalendarSearch, TbCalendarUp } from "react-icons/tb";
import { IoKeypadOutline } from "react-icons/io5";
import type { FunctionConfig } from "../../../services/agentService.ts";
import { useAppTheme } from "../../../ThemeContext.tsx";
import { EndCallModal } from "./EndCallModal.tsx";
import { TransferCallModal } from "./TransferCallModal.tsx";
import { CalendarAvailabilityModal } from "./CalendarAvailabilityModal.tsx";
import { BookAppointmentModal } from "./BookAppointmentModal.tsx";
import { PressDigitModal } from "./PressDigitModal.tsx";
import { CustomFunctionModal } from "./CustomFunctionModal.tsx";
import { AgentTransferModal } from "./AgentTransferModal.tsx";
import { ChevronDownIcon } from "@chakra-ui/icons";

// Define function types
type FunctionType =
  | "end_call"
  | "transfer_call"
  | "agent_swap"
  | "check_availability_cal"
  | "book_appointment_cal"
  | "press_digit"
  | "custom";

interface AgentFunctionsProps {
  agentId: string;
  llmId: string;
  functions: FunctionConfig[];
  onUpdate: (updatedFunctions: FunctionConfig[]) => void;
  isLoading: boolean;
}

export const AgentFunctions: React.FC<AgentFunctionsProps> = React.memo(
  ({ agentId, llmId, functions, onUpdate, isLoading }) => {
    const [selectedFunction, setSelectedFunction] =
      useState<FunctionType | null>(null);
    const [editingFunction, setEditingFunction] = useState<{
      index: number;
      config: FunctionConfig;
    } | null>(null);
    const { resolvedTheme } = useAppTheme();
    const isDark = resolvedTheme === "dark";

    const handleAddFunction = useCallback((type: FunctionType) => {
      setSelectedFunction(type);
      setEditingFunction(null);
    }, []);

    const handleEditFunction = useCallback(
      (index: number) => {
        const functionConfig = functions[index];
        setEditingFunction({ index, config: functionConfig });
        setSelectedFunction(functionConfig.type as FunctionType);
      },
      [functions]
    );

    const handleSaveFunction = useCallback(
      (newFunction: FunctionConfig) => {
        let updatedFunctions: FunctionConfig[];
        if (editingFunction !== null) {
          updatedFunctions = functions.map((f, i) =>
            i === editingFunction.index ? newFunction : f
          );
        } else {
          updatedFunctions = [...functions, newFunction];
        }
        onUpdate(updatedFunctions);
        setSelectedFunction(null);
        setEditingFunction(null);
      },
      [functions, onUpdate, editingFunction]
    );

    const handleRemoveFunction = useCallback(
      (index: number) => {
        const updatedFunctions = functions.filter((_, i) => i !== index);
        onUpdate(updatedFunctions);
      },
      [functions, onUpdate]
    );

    const getFunctionIcon = useCallback((type: FunctionType) => {
      switch (type) {
        case "end_call":
          return <HiOutlinePhoneMissedCall size={16} />;
        case "transfer_call":
          return <VscCallOutgoing size={16} />;
        case "agent_swap":
          return <Users size={16} />;
        case "check_availability_cal":
          return <TbCalendarSearch size={16} />;
        case "book_appointment_cal":
          return <TbCalendarUp size={16} />;
        case "press_digit":
          return <IoKeypadOutline size={16} />;
        case "custom":
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M4.33337 14.6667V10"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M4.33337 3.33398V1.33398"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M11.6666 14.666V12.666"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M11.6666 6.00065V1.33398"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M6.33337 4.66732V8.66732C6.33337 9.40065 6.00004 10.0007 5.00004 10.0007H3.66671C2.66671 10.0007 2.33337 9.40065 2.33337 8.66732V4.66732C2.33337 3.93398 2.66671 3.33398 3.66671 3.33398H5.00004C6.00004 3.33398 6.33337 3.93398 6.33337 4.66732Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M13.6666 7.33333V11.3333C13.6666 12.0667 13.3333 12.6667 12.3333 12.6667H11C9.99996 12.6667 9.66663 12.0667 9.66663 11.3333V7.33333C9.66663 6.6 9.99996 6 11 6H12.3333C13.3333 6 13.6666 6.6 13.6666 7.33333Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
          );
        default:
          return <LayoutGrid size={16} />;
      }
    }, []);

    const handleCloseModal = useCallback(() => {
      setSelectedFunction(null);
      setEditingFunction(null);
    }, []);

    const functionsList = useMemo(
      () => (
        <VStack spacing={2} align="stretch">
          {functions.map((func, index) => (
            <Flex
              key={index}
              justify="space-between"
              align="center"
              p={1.5}
              bg={isDark ? "gray.700" : "gray.50"}
              borderRadius="md"
              minHeight="32px"
            >
              <HStack spacing={2} flex={1} overflow="hidden">
                {getFunctionIcon(func.type as FunctionType)}
                <Text fontSize="xs" isTruncated>
                  {func.name}
                </Text>
              </HStack>
              <HStack spacing={1} flexShrink={0}>
                <Tooltip label="Edit">
                  <IconButton
                    aria-label="Edit"
                    icon={<Pencil size={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => handleEditFunction(index)}
                    isDisabled={isLoading}
                    color={isDark ? "gray.300" : "gray.700"}
                    _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
                  />
                </Tooltip>
                <Tooltip label="Remove">
                  <IconButton
                    aria-label="Remove"
                    icon={<Trash2 size={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => handleRemoveFunction(index)}
                    isDisabled={isLoading}
                    color={isDark ? "gray.300" : "gray.700"}
                    _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          ))}
        </VStack>
      ),
      [
        functions,
        isDark,
        handleEditFunction,
        handleRemoveFunction,
        getFunctionIcon,
        isLoading,
      ]
    );

    // Options for the custom select with icons.
    interface Option {
      value: string;
      label: string;
      icon: React.ReactElement;
    }
    const selectOptions: Option[] = useMemo(
      () => [
        {
          value: "end_call",
          label: "End Call",
          icon: <HiOutlinePhoneMissedCall size={16} />,
        },
        {
          value: "transfer_call",
          label: "Call Transfer",
          icon: <VscCallOutgoing size={16} />,
        },
        {
          value: "agent_swap",
          label: "Agent Transfer",
          icon: <Users size={16} />,
        },
        {
          value: "check_availability_cal",
          label: "Check Calendar Availability (Cal.com)",
          icon: <TbCalendarSearch size={16} />,
        },
        {
          value: "book_appointment_cal",
          label: "Book on the Calendar (Cal.com)",
          icon: <TbCalendarUp size={16} />,
        },
        {
          value: "press_digit",
          label: "Press Digit (IVR Navigation)",
          icon: <IoKeypadOutline size={16} />,
        },
        {
          value: "custom",
          label: "Custom Function",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M4.33337 14.6667V10"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M4.33337 3.33398V1.33398"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M11.6666 14.666V12.666"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M11.6666 6.00065V1.33398"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M6.33337 4.66732V8.66732C6.33337 9.40065 6.00004 10.0007 5.00004 10.0007H3.66671C2.66671 10.0007 2.33337 9.40065 2.33337 8.66732V4.66732C2.33337 3.93398 2.66671 3.33398 3.66671 3.33398H5.00004C6.00004 3.33398 6.33337 3.93398 6.33337 4.66732Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M13.6666 7.33333V11.3333C13.6666 12.0667 13.3333 12.6667 12.3333 12.6667H11C9.99996 12.6667 9.66663 12.0667 9.66663 11.3333V7.33333C9.66663 6.6 9.99996 6 11 6H12.3333C13.3333 6 13.6666 6.6 13.6666 7.33333Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
          ),
        },
      ],
      []
    );

    return (
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={2}
            px={4}
            _hover={{ bg: isDark ? "gray.800" : "gray.200" }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M3.25 3.25H9.25V9.25H3.25V3.25ZM3.25 10.75H9.25V16.75H3.25V10.75ZM10.75 3.25H16.75V9.25H10.75V3.25ZM10.75 10.75H16.75V16.75H10.75V10.75ZM12.25 4.75V7.75H15.25V4.75H12.25ZM12.25 12.25V15.25H15.25V12.25H12.25ZM4.75 4.75V7.75H7.75V4.75H4.75ZM4.75 12.25V15.25H7.75V12.25H4.75Z"
                  fill="currentColor"
                ></path>
              </svg>
              Functions
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4} bg={isDark ? "gray.800" : "gray.100"}>
          <VStack spacing={3} align="stretch">
            <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"}>
              Enable your agent with capabilities such as calendar bookings,
              call termination, etc.
            </Text>
            {isLoading ? (
              <Spinner size="sm" />
            ) : (
              <>
                {functionsList}
                <Box>
                  {/* Use custom select with icons to add a function */}
                  <CustomSelectWithIcons
                    label="Add a function"
                    options={selectOptions}
                    onSelect={(value) => {
                      if (value) {
                        handleAddFunction(value as FunctionType);
                      }
                    }}
                    isDisabled={isLoading}
                    isDark={isDark}
                  />
                </Box>
              </>
            )}
          </VStack>
          {/* Render modals based on selected function */}
          <EndCallModal
            isOpen={selectedFunction === "end_call"}
            onClose={handleCloseModal}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
          <TransferCallModal
            isOpen={selectedFunction === "transfer_call"}
            onClose={handleCloseModal}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
          <AgentTransferModal
            isOpen={selectedFunction === "agent_swap"}
            onClose={handleCloseModal}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
            currentAgentId={agentId}
          />
          <CalendarAvailabilityModal
            isOpen={selectedFunction === "check_availability_cal"}
            onClose={handleCloseModal}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
          <BookAppointmentModal
            isOpen={selectedFunction === "book_appointment_cal"}
            onClose={handleCloseModal}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
          <PressDigitModal
            isOpen={selectedFunction === "press_digit"}
            onClose={handleCloseModal}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
          <CustomFunctionModal
            isOpen={selectedFunction === "custom"}
            onClose={handleCloseModal}
            onSave={handleSaveFunction}
            initialValues={editingFunction?.config}
          />
        </AccordionPanel>
      </AccordionItem>
    );
  }
);

// CustomSelectWithIcons Component
interface Option {
  value: string;
  label: string;
  icon: React.ReactElement;
}

interface CustomSelectWithIconsProps {
  label: string;
  options: Option[];
  onSelect: (value: string) => void;
  isDisabled?: boolean;
  isDark: boolean;
}

const CustomSelectWithIcons: React.FC<CustomSelectWithIconsProps> = ({
  label,
  options,
  onSelect,
  isDisabled,
  isDark,
}) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Derive theme-based styles
  const cardBg = isDark ? "gray.900" : "white";
  const cardComponentsBg = isDark ? "gray.800" : "gray.100";
  const textColor = isDark ? "white" : "gray.800";
  const borderColor = isDark ? "gray.700" : "gray.300";

  return (
    <Box>
      <Text mb={2} fontWeight="medium" color={textColor}>
        {label}
      </Text>
      <Menu>
        <MenuButton
          as={Button}
          ref={buttonRef}
          leftIcon={<Plus size={14} />}
          rightIcon={<ChevronDownIcon />}
          size="xs"
          variant="outline"
          isDisabled={isDisabled}
          bg="transparent"
          color={textColor}
          borderColor={borderColor}
          _hover={{ bg: cardBg }}
          _expanded={{ bg: cardBg }}
          width={"100%"}
        >
          Add
        </MenuButton>
        <MenuList
          bg={isDark ? "gray.800" : "gray.100"}
          borderColor={borderColor}
          minW={
            buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "100%"
          }
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onSelect(option.value)}
              icon={option.icon}
              _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
              bg={cardComponentsBg}
              color={textColor}
              fontSize={"sm"}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default AgentFunctions;
